import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/apiClient';

/**
 * useFilterOptions
 *
 * Loads:
 * - All projects visible to the Jira account
 * - Users (assignees/authors) — loaded globally, NOT scoped to selected projects
 * - Sprints for all selected projects (aggregated, debounced)
 *
 * @param {Object} activeJiraAccount - The active Jira account object
 * @param {Array<string>} selectedProjectKeys - Array of selected project keys
 * @param {number} debounceDelay - Delay in milliseconds for debouncing users/sprints fetch (default: 300ms)
 */
const useFilterOptions = (activeJiraAccount, selectedProjectKeys, debounceDelay = 300) => {
  const [filterOptions, setFilterOptions] = useState({
    projects: [],
    sprints: [],
    users: [],
    statuses: ['To Do', 'In Progress', 'Done', 'Blocked', 'PR Review'],
    priorities: ['Highest', 'High', 'Medium', 'Low', 'Lowest'],
    loading: false
  });

  const debounceTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Fetch projects only when account changes or on mount
  const loadProjects = useCallback(async () => {
    // early return if no active account
    if (!activeJiraAccount) {
      setFilterOptions(prev => ({
        ...prev,
        projects: [],
        users: [],
        sprints: []
      }));
      return;
    }

    try {
      const projectsData = await apiClient.get('/jira/projects')
        .catch(() => ({ data: [] }));

      setFilterOptions(prev => ({
        ...prev,
        projects: (projectsData.data || []).map(p => ({
          id: p.key || p.id,
          name: p.name || p.key
        }))
      }));
    } catch (error) {
      console.error('Error loading projects:', error);
      setFilterOptions(prev => ({
        ...prev,
        projects: []
      }));
    }
  }, [activeJiraAccount]);

  // Fetch users and sprints for selected projects with abort support
  const loadUsersAndSprints = useCallback(async (projectKeys) => {
    if (!activeJiraAccount) {
      setFilterOptions(prev => ({
        ...prev,
        users: [],
        sprints: [],
        loading: false
      }));
      return;
    }

    // If no projects selected, load all users without project filter
    // so worklog author can be searched without selecting a project first
    if (!projectKeys || projectKeys.length === 0) {
      setFilterOptions(prev => ({ ...prev, loading: true }));
      try {
        const result = await apiClient.get('/jira/users').catch(() => ({ data: [] }));
        const userMap = new Map();
        (result?.data || []).forEach(u => {
          const userId = u.account_id || u.accountId || u.id;
          const userName = u.display_name || u.displayName || u.name || u.email;
          if (!userId || !userName) return;
          // Skip bot/system accounts — they have accountType !== 'atlassian'
          if (u.accountType && u.accountType !== 'atlassian') return;
          userMap.set(userId, { id: userId, name: userName, projects: [] });
        });
        setFilterOptions(prev => ({
          ...prev,
          users: Array.from(userMap.values()),
          sprints: [],
          loading: false
        }));
      } catch {
        setFilterOptions(prev => ({ ...prev, users: [], sprints: [], loading: false }));
      }
      return;
    }

    // Cancel previous in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setFilterOptions(prev => ({ ...prev, loading: true }));

    try {
      // Users are NOT scoped by project — worklog author / issue assignee must
      // be selectable regardless of project. Per-project user scoping was
      // returning empty sets from Jira's assignable-users search. Fetch the
      // full user list once instead.
      const usersPromise = apiClient
        .get('/jira/users', { signal })
        .catch(err => {
          if (err.name === 'AbortError' || err.name === 'CanceledError') return null;
          return { data: [] };
        });
      const sprintPromises = projectKeys.map(projectKey =>
        apiClient.get('/jira/sprints', {
          params: { project_key: projectKey },
          signal
        })
          .catch(err => {
            // Don't treat aborted requests as errors
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
              return null;
            }
            return { data: [] };
          })
      );

      const [usersResult, sprintResults] = await Promise.all([
        usersPromise,
        Promise.all(sprintPromises),
      ]);

      // aborted → skip state update (a newer request is running)
      if (usersResult === null) return;
      const validSprintResults = sprintResults.filter(r => r != null);

      // Aggregate users, de-dupe by account id. Users are global (not project
      // scoped), so `projects: []` — the project-based pruning in
      // getUpdatedFiltersOnProjectChange will simply keep them (fail-safe).
      const userMap = new Map();
      (usersResult?.data || []).forEach(u => {
        const userId = u.account_id || u.accountId || u.id;
        const userName = u.display_name || u.displayName || u.name || u.email;
        if (!userId || !userName) return;
        if (u.accountType && u.accountType !== 'atlassian') return; // skip bots
        userMap.set(userId, { id: userId, name: userName, projects: [] });
      });
      const allUsers = Array.from(userMap.values());

      // Aggregate sprints and remove duplicates by id
      const sprintMap = new Map();
      validSprintResults.forEach(result => {
        if (result?.data) {
          result.data.forEach(s => {
            // Validate that we have the required fields
            if (!s.id || !s.name) {
              console.warn('Invalid sprint data:', s);
              return;
            }

            if (!sprintMap.has(s.id)) {
              sprintMap.set(s.id, {
                id: s.id,
                name: s.name,
                state: s.state
              });
            }
          });
        }
      });
      const allSprints = Array.from(sprintMap.values());

      setFilterOptions(prev => ({
        ...prev,
        users: allUsers,
        sprints: allSprints,
        loading: false
      }));
    } catch (error) {
      // If the request was aborted, don't update state (new request is in progress)
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('Request was cancelled');
        return;
      }

      console.error('Error loading users and sprints:', error);
      setFilterOptions(prev => ({
        ...prev,
        users: [],
        sprints: [],
        loading: false
      }));
    }
  }, [activeJiraAccount]);

  // Load projects when account changes
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Debounced loading of users and sprints when project selection changes
  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Abort any in-flight requests when selection changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const projectKeys = Array.isArray(selectedProjectKeys) && selectedProjectKeys.length > 0
      ? selectedProjectKeys
      : [];

    // If no projects selected, still load users (without project filter)
    // so worklog author filter works without a project selection
    if (projectKeys.length === 0) {
      debounceTimeoutRef.current = setTimeout(() => {
        loadUsersAndSprints([]);
      }, debounceDelay);
      return;
    }

    setFilterOptions(prev => ({ ...prev, loading: true }));

    // Debounce the actual API calls (wait debounceDelay ms after user stops selecting)
    debounceTimeoutRef.current = setTimeout(() => {
      loadUsersAndSprints(projectKeys);
    }, debounceDelay);

    // Cleanup timeout and abort controller on unmount or when dependencies change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedProjectKeys, loadUsersAndSprints, debounceDelay]);

  return { filterOptions, loadFilterOptions: loadProjects };
};

export default useFilterOptions;